import { z } from 'zod';
import { languages, type MemberRole, type PlatformRole } from '../enums.js';
import { emailSchema } from '../ids.js';
import type { ImpersonationDto } from './platform-impersonation.js';

/**
 * Contrat réseau du module `auth`, partagé mot pour mot entre le serveur et le front : une seule
 * façon de valider une entrée, un seul type pour la réponse.
 *
 * Schémas d'entrée écrits à la main et `.strict()` : une clé inconnue est refusée.
 * Jamais dérivés d'une table — un schéma dérivé laisse passer `platformRole`, `isActive` et le
 * reste des colonnes que seul le serveur a le droit de fixer.
 */

/** Douze caractères minimum (checklist sécurité V2), cent vingt-huit maximum pour borner le coût argon2. */
const passwordSchema = z.string().min(12).max(128);
const nameSchema = z.string().trim().min(1).max(100);
const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Code à six chiffres attendu');

/** Un code de secours : dix caractères alphanumériques, la casse est indifférente à la saisie. */
export const BACKUP_CODE_LENGTH = 10;
export const backupCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9]{10}$/, 'Code de secours à dix caractères attendu');

/**
 * Second facteur présenté à la connexion : soit le code temporel à six chiffres, soit un code de
 * secours à dix caractères. La longueur suffit à les distinguer, le client n'a donc qu'un champ à
 * proposer et le serveur n'a rien de plus à lui demander.
 */
export const loginSecondFactorSchema = z.union([totpCodeSchema, backupCodeSchema]);

const phoneSchema = z.string().trim().min(1).max(40);

export const registerSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    firstName: nameSchema,
    lastName: nameSchema,
    organizationName: z.string().trim().min(1).max(200).optional(),
    phone: phoneSchema.optional(),
    /** Numéro d'autorisation d'exercer. Va dans le profil praticien du cabinet créé. */
    authorizationNumber: z.string().trim().min(1).max(60).optional(),
    /**
     * Langue du cabinet à la création. Les trois langues du produit, rien d'autre.
     * Facultative pour les appelants qui ne la portent pas : le français, défaut de la colonne
     * `organizations.preferred_language`.
     *
     * C'est la même langue que celle du compte à cet instant. Elle décide de ce que rien ne
     * rattrape ensuite : la **langue du courriel de vérification**, parti avant que le praticien
     * ait pu régler quoi que ce soit, la **langue du catalogue** préchargé — données modifiables,
     * jamais retraduites —, et la **langue par défaut du cabinet** (`preferredLanguage`).
     */
    language: z.enum(languages).optional(),
  })
  .strict();
export type RegisterInputDto = z.infer<typeof registerSchema>;

export const loginSchema = z
  .object({
    email: emailSchema,
    /** Pas de minimum ici : un mot de passe court est un identifiant faux, pas une erreur de saisie. */
    password: z.string().min(1).max(128),
    totpCode: loginSecondFactorSchema.optional(),
  })
  .strict();

/**
 * Identité modifiable par le titulaire du compte : prénom, nom et téléphone. L'adresse en est
 * absente parce qu'elle sert d'identifiant de connexion, et `platformRole` parce qu'il n'appartient
 * qu'au serveur — ni l'une ni l'autre n'a sa place dans une entrée réseau.
 */
export const profileUpdateSchema = z
  .object({
    firstName: nameSchema,
    lastName: nameSchema,
    /** Chaîne vide acceptée pour effacer le numéro : elle devient `null` en base. */
    phone: phoneSchema.or(z.literal('')).optional(),
  })
  .strict();
export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;

export const switchOrganizationSchema = z.object({ organizationId: z.uuid() }).strict();

export const changePasswordSchema = z
  .object({ currentPassword: z.string().min(1).max(128), newPassword: passwordSchema })
  .strict();

export const forgotPasswordSchema = z.object({ email: emailSchema }).strict();

export const resetPasswordSchema = z
  .object({ token: z.string().min(20).max(200), newPassword: passwordSchema })
  .strict();

export const totpCodeBodySchema = z.object({ code: totpCodeSchema }).strict();

export const totpDisableSchema = z
  .object({ password: z.string().min(1).max(128), code: totpCodeSchema })
  .strict();

/**
 * Régénération du lot de codes de secours. Le mot de passe est exigé : obtenir dix codes de
 * connexion vaut une authentification, et un jeton d'accès volé ne doit pas suffire à s'en tailler
 * un jeu de rechange.
 */
export const totpBackupCodesSchema = z.object({ password: z.string().min(1).max(128) }).strict();

/** Vérification d'adresse email. Le jeton vient du lien reçu, il n'est jamais deviné. */
export const verifyEmailSchema = z.object({ token: z.string().min(20).max(200) }).strict();

/**
 * Demande de changement d'adresse. Le mot de passe est exigé : l'adresse **est** l'identifiant de
 * connexion, la changer revient à déplacer la serrure, et un jeton d'accès volé ne doit pas
 * suffire à emmener le compte ailleurs.
 */
export const emailChangeRequestSchema = z
  .object({ newEmail: emailSchema, password: z.string().min(1).max(128) })
  .strict();
export type EmailChangeRequestInput = z.infer<typeof emailChangeRequestSchema>;

/** Confirmation, depuis la **nouvelle** boîte : c'est elle qui prouve qu'on la relève. */
export const emailChangeConfirmSchema = z.object({ token: z.string().min(20).max(200) }).strict();
export type EmailChangeConfirmInput = z.infer<typeof emailChangeConfirmSchema>;

/* ------------------------------------------------------------------ */
/* Objets de transfert de sortie : construits à la main, champ par champ. */
/* ------------------------------------------------------------------ */

export interface AuthUserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  platformRole: PlatformRole;
  /** Jamais la graine, jamais la date : seulement le fait que le second facteur est actif. */
  totpEnabled: boolean;
  /**
   * Adresse vérifiée ou non. Aucun blocage fonctionnel en V2.0 : le front affiche un bandeau et
   * propose le renvoi du lien, rien de plus.
   */
  emailVerified: boolean;
  /**
   * Mot de passe temporaire posé par l'exploitation : l'écran conduit au formulaire de nouveau
   * mot de passe, et le serveur refuse tout le reste (`403 password_change_required`).
   */
  mustChangePassword: boolean;
}

export interface AuthOrganizationDto {
  id: string;
  name: string;
  role: MemberRole;
}

export interface IdentityDto {
  user: AuthUserDto;
  organizations: AuthOrganizationDto[];
  activeOrganizationId: string | null;
  /**
   * Séance « Voir comme » en cours, nulle sinon. Pendant la séance, `user`, `organizations` et
   * `activeOrganizationId` sont ceux du compte **regardé** : l'écran se construit comme pour lui.
   */
  impersonation: ImpersonationDto | null;
}

/** Réponse de connexion et d'inscription. Le jeton d'accès vit en mémoire côté client. */
export interface AuthSessionDto extends IdentityDto {
  accessToken: string;
  expiresIn: number;
}

/** Réponse de rafraîchissement et de changement de cabinet : juste de quoi rejouer les appels. */
export interface AccessTokenDto {
  accessToken: string;
  expiresIn: number;
  activeOrganizationId: string | null;
}

export interface TotpSetupDto {
  /** À encoder en QR code par le client. */
  otpauthUrl: string;
  /** Même secret en base32, pour la saisie manuelle quand le QR ne peut pas être scanné. */
  secret: string;
}

/**
 * Le lot de codes de secours **en clair**, renvoyé une seule fois : à la génération et à
 * l'activation du second facteur. Le serveur n'en garde que l'empreinte, il ne saura donc jamais
 * les redonner — le client doit dire à l'utilisateur de les mettre à l'abri tout de suite.
 */
export interface TotpBackupCodesDto {
  codes: string[];
}

/** Ce qui reste du lot. Sert au bandeau « il vous reste n codes », jamais à retrouver un code. */
export interface TotpBackupCodesStatusDto {
  remaining: number;
}
